/**
 * Map sidebar — spec §F5.2: "332px, collapsible; body class `sb-open` drives dependent
 * widget offsets. Tabs (Indicators default)".
 *
 * Three columns of furniture now, after the 2026-08-22 client review:
 *
 * * **the domain rail** (R3) — a 46 px vertical strip of icon chips *left* of the panel,
 *   filtering both panes from one control;
 * * **the panes** (§F5.2) — `IndicatorsPane` and `LayersPane`, one per tab;
 * * **the analytics section** (R7) — pinned under the panes, describing whatever indicator
 *   is mapped at whatever scope is filtered, so the numbers are no longer behind a floating
 *   popover a reader has to know to open.
 *
 * The section is mounted **under the pane tabs rather than inside a pane** on purpose: it is
 * about the map, not about the list a reader happens to be looking at, and moving it into
 * the Indicators pane would make it vanish the moment they went to switch a layer on. The
 * panes scroll; the analytics section does not move with them.
 *
 * ## The tabs are a mode (client round 4 E2)
 *
 * Since E2 the two tabs are not two lists of the same map: switching to Layers clears the
 * mapped indicator, switching to Indicators clears the layers. The clearance is one store
 * action so every consumer follows it (`switchSidebarTab`); what lives here is the click
 * that calls it and the note that explains it, once per direction per page load.
 *
 * Collapse is the mockup's `#sidebar.collapsed { margin-left: -332px }` driven from the
 * §F1 store (`sidebarOpen`), with `#collapseSb` — the mockup's map button — as the toggle.
 * The rail slides out with the panel: it filters the panes, so a rail standing over the map
 * with nothing to filter would be a control with no effect.
 *
 * **The whole dock is hidden when it is collapsed, not just the panel** (R6 fix). `aria-hidden`
 * sat on `#sidebar` alone while what actually slides off-screen is `#sbDock`, so the rail's
 * nine domain buttons — which live outside `#sidebar` — stayed in the Tab order and in the
 * accessibility tree at `margin-left:-400px`: a keyboard reader landed on nine controls they
 * could not see. `base.css` takes them out of the tab order with `visibility:hidden` at the
 * end of the slide (which is also what keeps the transition visible), and the attribute moves
 * to the node that is doing the sliding. `#sidebar` keeps its own copy: the e2e vocabulary and
 * the mockup's CSS both name it, and two true statements do not contradict each other.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../lib/icons';
import { toast } from '../../lib/toast';
import {
  selectIndicatorMapped,
  selectLayersMapped,
  useAppStore,
  type SidebarTab,
} from '../../lib/state';
import { SidebarAnalytics } from '../widgets/SidebarAnalytics';
import { DomainRail } from './DomainRail';
import { IndicatorsPane } from './IndicatorsPane';
import { LayersPane } from './LayersPane';
import { firstPaneClearance } from './paneSwitch';

export function Sidebar() {
  const { t } = useTranslation();
  const open = useAppStore((s) => s.sidebarOpen);
  const switchSidebarTab = useAppStore((s) => s.switchSidebarTab);
  const [tab, setTab] = useState<SidebarTab>('inds');

  /**
   * Client round 4 E2: the tab is a mode, so moving to one clears the other's mapping.
   *
   * The state change is the store's (`switchSidebarTab` — one write, so the legend, the
   * analytics, the status bar and the permalink all follow it). What is here is the part a
   * store action cannot do: reading *whether anything was actually cleared* before it is, and
   * saying so once per direction. The store is read through `getState()` rather than through
   * a subscription because this is a question asked at the moment of a click, and subscribing
   * to `overlays`/`layersOn` would re-render the whole sidebar on every change to either.
   */
  const goToTab = (next: SidebarTab) => {
    if (next === tab) return;
    const before = useAppStore.getState();
    const cleared =
      next === 'layers'
        ? selectIndicatorMapped(before) && 'indicator'
        : selectLayersMapped(before) && 'layers';
    setTab(next);
    switchSidebarTab(next);
    if (cleared && firstPaneClearance(cleared)) toast(t(`sidebar.${cleared}Cleared`));
  };

  // §F5.2: the body class is the hook every off-sidebar widget offsets against.
  useEffect(() => {
    document.body.classList.toggle('sb-open', open);
    return () => document.body.classList.remove('sb-open');
  }, [open]);

  return (
    <div id="sbDock" className={open ? undefined : 'collapsed'} aria-hidden={!open}>
      <DomainRail />
      <aside id="sidebar" className={open ? undefined : 'collapsed'} aria-hidden={!open}>
        <div id="sbTabs" role="tablist">
          <button
            type="button"
            role="tab"
            data-pane="inds"
            aria-selected={tab === 'inds'}
            className={tab === 'inds' ? 'active' : undefined}
            onClick={() => goToTab('inds')}
          >
            <Icon name="chart" size={15} />
            {t('sidebar.indicators')}
          </button>
          <button
            type="button"
            role="tab"
            data-pane="layers"
            aria-selected={tab === 'layers'}
            className={tab === 'layers' ? 'active' : undefined}
            onClick={() => goToTab('layers')}
          >
            <Icon name="layers" size={15} />
            {t('sidebar.layers')}
          </button>
        </div>

        {tab === 'inds' ? <IndicatorsPane /> : <LayersPane />}

        <SidebarAnalytics />
      </aside>
    </div>
  );
}

/**
 * The collapse button — mockup `#collapseSb`, a `.mapbtn` pinned top-left **inside**
 * `#mapwrap` (so it stays put when the pane slides out). §F5.10's search box takes the
 * slot to its right in Task 7.
 */
export function SidebarToggle() {
  const { t } = useTranslation();
  const open = useAppStore((s) => s.sidebarOpen);
  const setOpen = useAppStore((s) => s.setSidebarOpen);
  return (
    <button
      type="button"
      className="mapbtn"
      id="collapseSb"
      aria-expanded={open}
      aria-controls="sidebar"
      title={t(open ? 'sidebar.collapse' : 'sidebar.expand')}
      aria-label={t(open ? 'sidebar.collapse' : 'sidebar.expand')}
      onClick={() => setOpen(!open)}
    >
      <Icon name="layers" />
    </button>
  );
}

export default Sidebar;
